import React, { useEffect, useState } from "react";
import { apiGet, apiPost } from "../../utils/api";
import { Spinner, EmptyState, Badge, ProgressBar } from "../../components/ui";
import { FaCreditCard, FaExternalLinkAlt, FaLock, FaRupeeSign } from "react-icons/fa";
import { useModal } from "../../context/ModalContext";
import { useAuth } from "../../context/AuthContext";
import ManagedProxyAddonCard from "../../components/ManagedProxyAddonCard";

export default function BillingTab() {
  const modal = useModal();
  const { teamRole } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (teamRole === "member") return;
    apiGet("/api/me/subscription")
      .then((res) => {
        if (res?.success) setData(res);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [teamRole]);

  // Team members cannot access billing
  if (teamRole === "member") {
    return (
      <div>
        <h2 className="text-lg font-semibold text-ink-primary mb-5">Billing</h2>
        <EmptyState
          icon={FaLock}
          title="Access restricted"
          description="Only team admins can manage billing. Contact your workspace owner."
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-ink-muted">
        <Spinner size="sm" /> Loading billing info...
      </div>
    );
  }

  if (!data?.subscription || !data?.plan) {
    return (
      <div>
        <h2 className="text-lg font-semibold text-ink-primary mb-5">Billing</h2>
        <EmptyState
          icon={FaCreditCard}
          title="No active subscription"
          description="You are not subscribed to any plan."
        />
        <div className="flex gap-3 mt-4">
          <a href="/pricing" className="btn-primary inline-block">
            View Plans & Subscribe
          </a>
          <a href="/pricing/inr" className="btn-secondary inline-flex items-center gap-1.5">
            <FaRupeeSign size={12} /> Pay in INR
          </a>
        </div>
        <div className="mt-6"><ManagedProxyAddonCard /></div>
      </div>
    );
  }

  const { subscription: sub, plan, usageByKey } = data;
  const renewalDate = sub.interval === 'lifetime'
    ? "Lifetime Access"
    : sub.currentPeriodEnd
      ? new Date(sub.currentPeriodEnd).toLocaleDateString()
      : "N/A";

  const handleOpenPortal = async () => {
    try {
      setPortalLoading(true);
      const res = await apiPost("/api/customer-portal");
      if (res?.url) window.location.href = res.url;
      else modal.alert("Unable to open billing portal.", "warning");
    } catch (e) {
      console.error(e);
      modal.alert(e?.message || "Failed to open billing portal.", "error");
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCancel = async () => {
    const confirmed = await modal.confirm(
      "Are you sure you want to cancel your subscription? You'll retain access until the end of your billing period.",
      { danger: true, confirmText: "Cancel Subscription" }
    );
    if (!confirmed) return;

    try {
      setCancelling(true);
      const res = await apiPost("/api/cancel");
      if (res?.success) {
        modal.alert("Subscription will cancel at end of billing period.", "success");
        // Refresh data
        const refreshed = await apiGet("/api/me/subscription");
        if (refreshed?.success) setData(refreshed);
      } else {
        modal.alert(res?.message || "Failed to cancel.", "error");
      }
    } catch (e) {
      console.error(e);
      modal.alert(e?.message || "Failed to cancel subscription.", "error");
    } finally {
      setCancelling(false);
    }
  };

  const isTrial = !!sub.isTrial;

  return (
    <div>
      <h2 className="text-lg font-semibold text-ink-primary mb-5">Billing Information</h2>

      <div className="mb-6"><ManagedProxyAddonCard /></div>

      {isTrial && (
        <div className="mb-5 p-4 rounded-lg border border-amber-300 bg-amber-50 flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-amber-900">
              You're on a Pro Trial
              {typeof sub.trialDaysLeft === "number" && ` — ${sub.trialDaysLeft} day${sub.trialDaysLeft === 1 ? "" : "s"} left`}
            </p>
            <p className="text-xs text-amber-800 mt-1">
              Trial ends {renewalDate}. Pick a plan before then to keep access.
            </p>
          </div>
          <a href="/pricing" className="btn-primary whitespace-nowrap">Choose a plan</a>
        </div>
      )}

      {/* Plan info cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="p-4 rounded-lg border border-surface-border">
          <p className="text-xs text-ink-muted mb-1">Plan</p>
          <p className="font-semibold text-ink-primary">
            {plan.name}{isTrial && <span className="ml-2 text-xs font-medium text-amber-700">(Trial)</span>}
          </p>
        </div>
        <div className="p-4 rounded-lg border border-surface-border">
          <p className="text-xs text-ink-muted mb-1">Status</p>
          <div className="flex items-center gap-2">
            <Badge variant={isTrial ? "warning" : sub.status === "active" ? "success" : "danger"}>
              {isTrial ? "trial" : sub.status}
            </Badge>
            {sub.cancelAtPeriodEnd && (
              <span className="text-xs text-red-500">Cancels on {renewalDate}</span>
            )}
          </div>
        </div>
        <div className="p-4 rounded-lg border border-surface-border">
          <p className="text-xs text-ink-muted mb-1">Billing Interval</p>
          <p className="font-semibold text-ink-primary capitalize">{sub.interval || "month"}ly</p>
        </div>
        <div className="p-4 rounded-lg border border-surface-border">
          <p className="text-xs text-ink-muted mb-1">
            {sub.cancelAtPeriodEnd ? "Access Until" : isTrial ? "Trial Ends" : "Renewal Date"}
          </p>
          <p className="font-semibold text-ink-primary">{renewalDate}</p>
        </div>
      </div>

      {/* Usage progress bars */}
      {usageByKey && Object.keys(usageByKey).length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-ink-primary mb-3">Usage This Period</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(usageByKey).map(([key, u]) => (
              <div key={key} className="p-4 rounded-lg border border-surface-border">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-ink-secondary capitalize">
                    {key.replace(/_/g, " ")}
                  </span>
                  <span className="text-ink-muted">
                    {u.type === "bool" ? (
                      u.total >= 1 || u.total === -1 ? (
                        <Badge variant="success">Enabled</Badge>
                      ) : (
                        <Badge variant="danger">Not Available</Badge>
                      )
                    ) : (
                      `${u.used} / ${u.total === -1 ? "Unlimited" : u.total}`
                    )}
                  </span>
                </div>
                {u.type !== "bool" && u.total > 0 && (
                  <ProgressBar value={u.used} max={u.total} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="btn-primary flex items-center gap-2"
          onClick={handleOpenPortal}
          disabled={portalLoading}
        >
          {portalLoading ? <Spinner size="sm" /> : <FaExternalLinkAlt size={12} />}
          Manage Subscription
        </button>
        <a href="/pricing" className="btn-secondary">
          {sub.cancelAtPeriodEnd ? "Resubscribe" : "Upgrade Plan"}
        </a>
        <a href="/pricing/inr" className="btn-secondary flex items-center gap-1.5">
          <FaRupeeSign size={12} /> Pay in INR
        </a>
        {sub.status === "active" && !sub.cancelAtPeriodEnd && !isTrial && (
          <button
            className="btn-danger"
            onClick={handleCancel}
            disabled={cancelling}
          >
            {cancelling ? <><Spinner size="sm" /> Cancelling...</> : "Cancel Subscription"}
          </button>
        )}
      </div>
    </div>
  );
}
